import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import {
  Sitemap,
  PageDescription,
  SitemapV,
  PageAISettings,
  ListOfSectionsForPageV,
  ListOfSectionsForPage
} from "@/types/singularity";
import { pipe } from "fp-ts/function";
import * as t from "io-ts";
import { fold } from "fp-ts/Either";
import { requestStructuredOutput } from "@/lib/ai/structured-outputs";
import { jsonStringifyOrUndefined } from "@/util/json-stringify-or-undefined";
import { globalStore } from "@/store/store";
import { getSectionTypes } from "@/lib/singularity/singularity";
import { loadSingularitySectionLibrary } from "./page-generation";
import { processCompletion } from "../ai/ai";
import { addAiJobAndGetValueReturnedByCallback } from "@/futurelayer/ai-job-manager/ai-jobs";

export async function generateSitemap(
  additionalPromptInfo = ""
): Promise<Sitemap | undefined> {
  // todo - loadSingularitySectionLibrary should happen elsewhere, not in this function.
  await loadSingularitySectionLibrary();

  const websiteName = globalStore.aiSettings.prompt.businessName ?? "";
  const websiteDescription = globalStore.aiSettings.prompt.businessDescription ?? "";

  const sectionTypesString = getSectionTypes()
    .map(sectionType => `'${sectionType}'`)
    .join(", ");

  var prompt = ``;

  if (!additionalPromptInfo) {
    prompt = `Generate a sitemap for a website.

          The website should have an appropriate number of pages for the content. If the prompt includes information about the services offered, consider individual pages for each service.

          Each page should have an appropriate number of sections for its type. Maybe 8 for the homepage, and just one or two for the contact page.
          
          Website name: ${websiteName}
          Website description: ${websiteDescription}

          Valid values for 'type' are ${sectionTypesString}. It's important you don't use any other values for 'type'.`;
  } else {
    prompt = `Generate a sitemap for a website.

          ${
            additionalPromptInfo
              ? `
                  ${additionalPromptInfo}
                  `
              : ""
          }

          Valid values for 'type' are ${sectionTypesString}. It's important you don't use any other values for 'type'.`;
  }

  const result = await addAiJobAndGetValueReturnedByCallback(
    async (abortController) => {
      return await requestStructuredOutput(prompt, SitemapV, abortController, true);
    },
    {
      jobType: 'sitemap_generation',
      businessName: websiteName,
      businessDescription: websiteDescription,
      description: `Generating sitemap for ${websiteName}`,
      priority: 'critical'
    }
  );

  return result;
}

export async function generateListOfSectionsForPage(
  pageTitle: string,
  additionalInfoForPrompt: string = ""
): Promise<ListOfSectionsForPage | undefined> {
  const websiteName = globalStore.aiSettings.prompt.businessName;
  const websiteDescription = globalStore.aiSettings.prompt.businessDescription;

  const sectionTypesString = getSectionTypes()
    .map(sectionType => `'${sectionType}'`)
    .join(", ");

  const result = await addAiJobAndGetValueReturnedByCallback(
    async (abortController) => {
      return await requestStructuredOutput(
        `Generate a list of sections that will go on a website page.

          The page should have an appropriate number of sections. Maybe 8 for a homepage, 3 or 4 for a services page, and just one or two for the contact page. Feel free to deviate from this if you think it makes sense.

          Website name: ${websiteName}
          Website description: ${websiteDescription}

          The page title is: ${pageTitle}

          ${additionalInfoForPrompt}
          
          Valid values for 'type' are ${sectionTypesString}. It's important you don't use any other values for 'type'.`,
        ListOfSectionsForPageV,
        abortController,
        true
      );
    },
    {
      jobType: 'background_page_generation',
      pageId: 0,
      pageTitle: pageTitle,
      description: `Generating section list for page: ${pageTitle}`,
      priority: 'critical',
      dontCreateSnapshot: true
    }
  );

  return result;
}

export async function createPageInWP(page: PageDescription) {
  const aiSettings: PageAISettings = {
    pageDescription: page,
    shouldGenerateOnInitialLoad: true
  };

  const aiSettingsAsJsonString = jsonStringifyOrUndefined(aiSettings);

  return pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_insert_post",
      friendlyName: "Insert Post",
      dataToPost: {
        postTitle: page.title,
        postType: "page",
        aiSettings: aiSettingsAsJsonString ? aiSettingsAsJsonString : false
      },
      decoder: t.type({
        postId: t.number
      })
    }),
    fold(
      () => undefined,
      data => {
        return {
          id: data.postId,
          title: page.title
        };
      }
    )
  );
}
